/**
 * <AvatarStack> — quelques visages qui se chevauchent, et qui ils sont (molecule).
 *
 * Les autres élèves d'un live : avant d'y entrer, ceux qui sont déjà connectés à la salle ; dans
 * la scène, ceux du cours partagé. Deux visages au plus, puis « +N » (l'ancien groupe d'avatars en
 * montrait deux) ; chacun est détouré de la couleur de la surface, pour que le chevauchement se lise
 * sur une carte comme sur la nuit. À côté, la phrase que l'écran écrit (« Deux autres élèves sont
 * déjà là »).
 *
 * On ne nomme jamais un autre élève, on montre son avatar (Wajih, 17/10, comme aujourd'hui) : un
 * visage n'a donc pas de nom, et sans image il prend une pastille neutre, pas une initiale.
 *
 * Ni bouton ni menu : l'ancien appui ouvrait la liste des noms.
 *
 * Composes DS pieces only — `<Avatar>`, `<Text>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Text } from "../../atoms/typography/Text"
import { type UINamedStackPerson, type UIStackPerson } from "../../../ports/primary/ui/types/studentLive"

// Un visage, sans nom : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UINamedStackPerson, UIStackPerson }

export type AvatarStackProps = {
    /** Des visages sans nom (un élève voit les autres) ou nommés (le masteur voit ses élèves). */
    people: ReadonlyArray<UIStackPerson | UINamedStackPerson>
    /** Ce qui se lit à côté — « Deux autres élèves sont déjà là ». Absent : les visages seuls. */
    label?: string
    /** Visages montrés avant « +N ». Défaut 2. */
    max?: number
    /** Diamètre, en px. Défaut 34, la plus petite taille du livre. */
    size?: number
    /**
     * Ce que dit un lecteur d'écran quand aucune phrase ne s'affiche (les autres élèves d'un live :
     * combien ils sont, pas leurs prénoms). Défaut : `label`.
     */
    accessibilityLabel?: string
    testID?: string
}

/** Le détourage de chaque visage, à la couleur de ce sur quoi la pile est posée. */
const RING = 2

export function AvatarStack(props: AvatarStackProps) {
    const { people, label, max = 2, size = 34, accessibilityLabel = label, testID } = props

    if (people.length === 0) return null

    const shown = people.slice(0, max)
    const more = people.length - shown.length
    const overlap = Math.round(size / 3)

    return (
        <View
            style={styles.row}
            testID={testID}
            accessible={accessibilityLabel != null}
            accessibilityLabel={accessibilityLabel}
        >
            <View style={styles.faces}>
                {shown.map((person, index) => (
                    <View key={person.id} style={styles.ring(size, index === 0 ? 0 : -overlap)}>
                        {/* Sans nom, le repli d'`<Avatar>` est la silhouette neutre, photo absente ou expirée ;
                            le masteur, lui, connaît ses élèves : avec un prénom, son initiale. */}
                        <Avatar uri={person.uri} name={"name" in person ? person.name : undefined} size={size} />
                    </View>
                ))}
                {more > 0 && (
                    <View style={[styles.ring(size, -overlap), styles.more(size)]}>
                        <Text size="text-sm" weight="bold" color="text-primary">
                            {`+${more}`}
                        </Text>
                    </View>
                )}
            </View>
            {label != null && (
                <Text size="text-md" color="text-muted" style={styles.label}>
                    {label}
                </Text>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    faces: {
        flexDirection: "row",
        alignItems: "center",
    },
    ring: (size: number, marginLeft: number) => ({
        width: size + RING * 2,
        height: size + RING * 2,
        borderRadius: (size + RING * 2) / 2,
        borderWidth: RING,
        borderColor: theme.colors["surface-raised"],
        marginLeft,
        overflow: "hidden" as const,
    }),
    more: (size: number) => ({
        width: size + RING * 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors["initials-fill"],
    }),
    label: {
        flexShrink: 1,
    },
}))
